import { useState } from "react";
import Pagination from "./Pagination";
import { useDebouncedEffect } from "../../Hooks/useDebouncedEffect";
import TableBodyHandler from "./TableBodyHandler";
import "./Table.css";

export default function Table({
  children,
  showTableName,
  tableName,
  tableTitles,
  showEntries,
  sortBy,
  dateFilter,
  from,
  setFrom,
  to,
  setTo,
  perPage,
  setPerPage,
  status,
  setStatus,
  status_options,
  setSearchString,
  isLoading,
  isFetching,
  data,
  totalPages,
  totalDocs,
  page = 1,
  setPage,
  nosearch,
  sortColumnFields,
  sortField,
  sortOrder,
  onSortColumn,
  lastColumnIsAction = true,
}) {
  const [internal_search, setInternalSearch] = useState("");

  useDebouncedEffect(
    () => setSearchString && setSearchString(internal_search),
    [internal_search],
    500
  );

  const recordCount = Array.isArray(data)
    ? data.length
    : data?.docs?.length ?? 0;

  const panelClassName = [
    "table-panel",
    recordCount === 0 ? "table-panel--empty" : "",
    recordCount > 0 && recordCount <= 5 ? "table-panel--compact" : "",
    lastColumnIsAction ? "" : "table-panel--data-last-col",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {/* <div className="card-content collapse show dashCard pt-5 px-5"> */}
      {showTableName && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="back-title">
              <div className="pageTitleInner">
                <h1 className="pageTitle text-capitalize m-0">
                  {tableName}
                  {/* Users Management */}
                </h1>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="row mb-2">
        <div className="col-12">
          <div className={panelClassName}>
            {(dateFilter || showEntries || sortBy || !nosearch) && (
              <div className="table-toolbar">
                <div className="table-toolbar__row">
                  {dateFilter && (
                    <div className="table-toolbar__group">
                      <label className="table-toolbar__label">Sort By Date</label>
                      <div className="table-toolbar__dates">
                        <input
                          className="table-toolbar__input"
                          type="date"
                          value={from}
                          onChange={({ target }) => setFrom(target.value)}
                        />
                        <input
                          className="table-toolbar__input"
                          type="date"
                          value={to}
                          onChange={({ target }) => setTo(target.value)}
                        />
                      </div>
                    </div>
                  )}

                  {!nosearch && (
                    <div className="table-toolbar__group table-toolbar__group--search">
                      <div className="table-toolbar__search">
                        <input
                          type="search"
                          id="search-inp"
                          className="table-toolbar__input"
                          placeholder="Search"
                          value={internal_search}
                          onChange={({ target }) =>
                            setInternalSearch(target.value)
                          }
                        />
                        <button type="button" className="table-toolbar__search-btn">
                          <i className="fas fa-search" />
                        </button>
                      </div>
                    </div>
                  )}

                  {showEntries && (
                    <div className="table-toolbar__group table-toolbar__group--inline">
                      <label className="table-toolbar__label">Show</label>
                      <select
                        className="table-toolbar__select table-toolbar__select--sm"
                        value={perPage}
                        onChange={({ target }) =>
                          setPerPage && setPerPage(target.value)
                        }
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="table-toolbar__hint">Entries</span>
                    </div>
                  )}

                  {sortBy && (
                    <div className="table-toolbar__group table-toolbar__group--inline">
                      <label className="table-toolbar__label">Filter By</label>
                      <select
                        className="table-toolbar__select table-toolbar__select--filter"
                        value={status}
                        onChange={({ target }) => setStatus(target.value)}
                      >
                        <option value="">All</option>
                        {status_options && status_options?.length > 0 ? (
                          status_options?.map((statusOption) => (
                            <option value={statusOption?.value} key={statusOption?.label}>
                              {statusOption?.label}
                            </option>
                          ))
                        ) : (
                          <>
                            <option value={true}>Active</option>
                            <option value={false}>Inactive</option>
                          </>
                        )}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="maain-tabble table-responsive">
              <div className="table-scroll-inner">
                <table className="table">
                  <thead>
                    <tr>
                      {tableTitles?.map((title, index) => {
                        const field = sortColumnFields?.[index];
                        const sortable =
                          field &&
                          typeof onSortColumn === "function" &&
                          sortField !== undefined &&
                          sortOrder;
                        if (!sortable) {
                          return <th key={index}>{title}</th>;
                        }
                        const active = sortField === field;
                        return (
                          <th key={index} className="text-nowrap">
                            <button
                              type="button"
                              className="border-0 bg-transparent p-0 text-start w-100 dashLabel"
                              style={{ cursor: "pointer" }}
                              onClick={() => onSortColumn(field)}
                            >
                              {title}
                              {active ? (
                                <i
                                  className={`fas fa-sort-${sortOrder === "asc" ? "up" : "down"
                                    } ms-1`}
                                  aria-hidden
                                />
                              ) : (
                                <i
                                  className="fas fa-sort ms-1 opacity-50"
                                  style={{ fontSize: "0.85em" }}
                                  aria-hidden
                                />
                              )}
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    <TableBodyHandler
                      data={data}
                      length={tableTitles?.length}
                      isLoading={isLoading}
                      isFetching={isFetching}
                    />
                    {children}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="table-panel__footer row align-items-center g-0">
              <Pagination
                currentDocs={recordCount}
                totalDocs={totalDocs}
                totalPages={totalPages}
                page={page}
                setPage={setPage}
              />
            </div>
          </div>
        </div>
      </div>
      {/* </div> */}
    </>
  );
}
